import { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@material-ui/core";
import Menu from "../menu/menu";
import { api } from "../../services/apiService";
import { CONSTANTS } from "../../constants/app-constant";
import QuestionTagsSection from "./question-tags-section";
import PostSessionQuizzesSection from "./post-session-quizzes-section";
import RecentActivitySection from "./recent-activity-section";
import usePostSessionQuizzes from "./use-post-session-quizzes";
import { directoryEntries } from "./student-directory";
import "./mentorships.scss";
import usePageChoice, { studentProgressLocation } from "./use-page-choice";

// The directory starts short so recent activity stays the first thing a tutor
// with many students sees; search always covers every linked student.
const ROSTER_PREVIEW = 6;

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

function ProgressCardContent({ onOpen, children }) {
  if (!onOpen) return <div className="mtr-item-main">{children}</div>;
  return <button type="button" className="mtr-item-main mtr-progress-button" onClick={onOpen}>{children}</button>;
}

function InvitationPanel({ isTutor, children }) {
  return isTutor ? <details className="mtr-card mtr-request mtr-invitation">
    <summary>Invite a student</summary>
    <p className="mtr-sub">Your assigned Medlearnity students connect automatically. Use an invitation for an additional student.</p>
    {children}
  </details> : <div className="mtr-card mtr-request">
    <Typography variant="subtitle1" className="mtr-card-title">Invite a tutor</Typography>
    {children}
  </div>;
}

function studentCountLabel(count) {
  if (count <= 0) return "Students";
  return count === 1 ? "1 student" : `${count} students`;
}

function AdminStudentCard({ student, history }) {
  const { studentUserId, displayName, email } = student;
  const openProgress = studentUserId ? () => history.push(`/mentorships/students/${studentUserId}`) : null;
  const label = displayName || email || "A student";
  return (
    <div className={`mtr-card mtr-item${openProgress ? " mtr-item--link" : ""}`}>
      <ProgressCardContent onOpen={openProgress}>
        <Typography component="span" className="mtr-name">{label}</Typography>
        {email && displayName && <Typography component="span" variant="caption" className="mtr-sub">{email}</Typography>}
        <Typography component="span" variant="caption" className="mtr-sub">
          {openProgress ? "View-only · tap to see progress" : "View-only · connected"}
        </Typography>
      </ProgressCardContent>
    </div>
  );
}

/**
 * Flag-gated tutor<->student mentorships (phase 1: view-only follow +
 * request/accept). Self-contained — holds its own state and calls the
 * apiService directly (the /mentorships endpoints per
 * docs/qlearn-tutor-student-sharing-design.md). Inert behind
 * CONSTANTS.mentorshipsEnabled until the backend lands; the list call 404s
 * gracefully meanwhile.
 */
export default function MentorshipsPage() {
  const history = useHistory();
  const role = typeof window !== "undefined" ? window.localStorage.getItem("role") : null;
  const isTutor = role === "ROLE_TUTOR";
  // Admin-Status accounts get an oversight branch: a server-paged, searchable
  // roster of EVERY student, each opening the same read-only progress panel.
  // No tutor-student link required; the server (ROLE_ADMIN) is the gate.
  const isAdmin = role === "ROLE_ADMIN";
  const myParty = isTutor ? "TUTOR" : "STUDENT";
  // Tutors follow students; students ask a tutor to follow them.
  const counterpartLabel = isTutor ? "student" : "tutor";

  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [rosterSearch, setRosterSearch] = usePageChoice("rosterSearch", "", isTutor);
  const [rosterExpanded, setRosterExpanded] = usePageChoice("rosterExpanded", false, isTutor);
  // The one OPEN-tag list call (§10.3-R1/R2: server-scoped to tags addressed to
  // the caller over LIVE links). It feeds the roster counts (T4) and the recent
  // activity feed. status: loading | ok | dark | error.
  const tagsEnabled = isTutor && CONSTANTS.mentorshipTagsEnabled;
  const [openTags, setOpenTags] = useState({ status: "loading", tags: [] });
  const tagsSeq = useRef(0);
  // One post-session request shared by the activity feed and the email tracker.
  const postSessionEnabled = isTutor && CONSTANTS.tutorPostSessionQuizzesEnabled;
  const postSession = usePostSessionQuizzes(postSessionEnabled);

  // ---- admin all-students roster (server-paged + search) ----------------
  const ADMIN_PAGE_SIZE = 20;
  const [students, setStudents] = useState(null);
  const [adminSearch, setAdminSearch] = useState("");
  const [adminPage, setAdminPage] = useState(0);
  const [adminTotalPages, setAdminTotalPages] = useState(0);
  const [adminTotalElements, setAdminTotalElements] = useState(0);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState("");

  const loadStudents = useCallback(async (page, search) => {
    setAdminLoading(true);
    setAdminError("");
    try {
      const res = await api.listAllStudentsAdmin({
        page,
        size: ADMIN_PAGE_SIZE,
        search: search || undefined,
      });
      const status = res?.errStatus || res?.status;
      if (status === 403) {
        // Server guard is the gate (anti-pattern watch: never client-only).
        setAdminError("Admin access is required to view all students.");
        setStudents([]);
        return;
      }
      if (status === 404) {
        // Mentorship feature dark — empty, not an error.
        setStudents([]);
        return;
      }
      // Spring Page envelope { content, totalPages, totalElements, ... }.
      if (Array.isArray(res?.content)) {
        setStudents(res.content);
        setAdminTotalPages(Number.isFinite(res.totalPages) ? res.totalPages : 0);
        setAdminTotalElements(
          Number.isFinite(res.totalElements) ? res.totalElements : res.content.length
        );
      } else {
        setAdminError("Couldn't load students. Please try again.");
        setStudents(null);
      }
    } catch (e) {
      setAdminError("Couldn't load students. Please try again.");
      setStudents(null);
    } finally {
      setAdminLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    // Debounce keystrokes (350ms); page changes fetch immediately.
    const t = setTimeout(() => loadStudents(adminPage, adminSearch.trim()), adminSearch ? 350 : 0);
    return () => clearTimeout(t);
  }, [isAdmin, adminPage, adminSearch, loadStudents]);

  const loadTags = useCallback(async () => {
    const seq = ++tagsSeq.current;
    setOpenTags({ status: "loading", tags: [] });
    let next = { status: "error", tags: [] };
    try {
      const res = await api.listQuestionTags({ status: "OPEN" });
      // A dark 404 is the same single seam every mentorship call shares.
      if ((res?.errStatus || res?.status) === 404) next = { status: "dark", tags: [] };
      else if (Array.isArray(res)) next = { status: "ok", tags: res };
    } catch (e) {
      // stays "error": the feed says so; the roster just shows no counts
    }
    if (seq === tagsSeq.current) setOpenTags(next);
  }, []);

  useEffect(() => {
    if (!tagsEnabled) return undefined;
    loadTags();
    return () => { tagsSeq.current += 1; };
  }, [tagsEnabled, loadTags]);

  // T4: per-student OPEN-tag counts. A dark or failed fetch leaves the roster
  // without counts — no error UI there (the feed reports the failure).
  const openTagCounts = openTags.status === "ok" ? openTags.tags.reduce((counts, t) => {
    if (t?.studentUserId) counts[t.studentUserId] = (counts[t.studentUserId] || 0) + 1;
    return counts;
  }, {}) : null;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.listMentorships();
      const status = res?.errStatus || res?.status;
      if (status === 404) {
        // Backend not enabled yet — treat as an empty list, not an error.
        setList([]);
        return;
      }
      // Only treat a real array (or {mentorships:[...]}) as data. doGetCall
      // resolves {status, errMsg} for any non-200 (even a bodyless one) and a
      // raw Error only on a network drop — both must surface as an error here,
      // not an empty list.
      if (Array.isArray(res)) {
        setList(res);
      } else if (Array.isArray(res?.mentorships)) {
        setList(res.mentorships);
      } else {
        setError("Couldn't load your mentorships. Please try again.");
        setList(null);
      }
    } catch (e) {
      setError("Couldn't load your mentorships. Please try again.");
      setList(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Admins use the roster loader above; the tutor/student link list does not
    // apply to them.
    if (!isAdmin) load();
  }, [load, isAdmin]);

  const respond = async (id, op) => {
    setBusyId(id);
    setError("");
    let failed = false;
    try {
      // doUpdateCall never rejects — it RESOLVES {errStatus, errMsg} on failure,
      // so we must inspect the resolved value, not rely on catch.
      const res = await api.respondToMentorship(id, op);
      failed = !!(res?.errStatus || res?.errMsg);
      if (!failed && res?.id) {
        // Consume the updated DTO (the backend contract pins a non-empty body):
        // patch the row in place so the new status shows immediately; the
        // refetch below stays authoritative for everything else.
        setList((cur) => (Array.isArray(cur) ? cur.map((m) => (m?.id === res.id ? { ...m, ...res } : m)) : cur));
      }
    } catch (e) {
      failed = true;
    }
    // Reload first (load() clears the error banner), then surface the failure so
    // it isn't wiped by the refetch — a silent failure would just show stale state.
    // Keep the row busy across the refetch so a fast second click can't fire a
    // duplicate mutation against the stale list.
    await load();
    setBusyId("");
    if (failed) setError("Couldn't update that request. Please try again.");
  };

  const canSend = isValidEmail(email.trim()) && !submitting;

  const sendRequest = async () => {
    if (!canSend) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await api.requestMentorship({
        counterpartEmail: email.trim(),
        message: message.trim() || undefined,
      });
      const status = res?.errStatus || res?.status;
      if (status === 404) {
        // Uniform server 404 (QLS #526): unknown email AND non-tutor answer
        // identically, so the copy must cover both without distinguishing them.
        setError("No tutor found for that email.");
        return;
      }
      if (res?.errStatus || res?.errMsg) {
        setError("Couldn't send that request. Check the email and try again.");
        return;
      }
      setEmail("");
      setMessage("");
      load();
    } catch (e) {
      setError("Couldn't send that request. Check the email and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const items = list || [];
  // Incoming = a PENDING the OTHER party sent me (I accept/decline). A missing
  // initiatedBy also lands here (actionable) rather than vanishing from every bucket.
  const incoming = items.filter((m) => m.status === "PENDING" && m.initiatedBy !== myParty);
  const outgoing = items.filter((m) => m.status === "PENDING" && m.initiatedBy === myParty);
  const active = items.filter((m) => m.status === "ACTIVE");
  const rosterQuery = isTutor ? rosterSearch.trim().toLowerCase() : "";
  // Tutors get a sorted directory where duplicated names carry a secondary
  // identity; students keep their tutor list as the server returned it.
  const directory = isTutor ? directoryEntries(active) : active.map((link) => ({ link, secondary: "" }));
  const labels = new Map(directory.filter((e) => e.link.studentUserId)
    .map((e) => [e.link.studentUserId, { name: e.name, secondary: e.secondary }]));
  const matching = directory.filter(({ link: m }) => !rosterQuery ||
    [m.counterpartDisplayName, m.counterpartEmail].some(value => (value || "").toLowerCase().includes(rosterQuery)));
  const rosterCapped = isTutor && !rosterQuery && !rosterExpanded && matching.length > ROSTER_PREVIEW;
  const visibleActive = rosterCapped ? matching.slice(0, ROSTER_PREVIEW) : matching;

  // MentorshipDto is FLAT (counterpartDisplayName/counterpartEmail/counterpartUserId
  // + tutorUserId/studentUserId) — there is no nested counterpart object.
  const name = (m) => m.counterpartDisplayName || m.counterpartEmail || `A ${counterpartLabel}`;

  const invitation = (
    <InvitationPanel isTutor={isTutor}>
      <div className="mtr-request-row">
        <TextField
          className="mtr-field"
          size="small"
          variant="outlined"
          type="email"
          placeholder={`${counterpartLabel}@example.com`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          inputProps={{ "aria-label": `${counterpartLabel} email`, "data-testid": "mtr-email" }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={sendRequest}
          disabled={!canSend}
          data-testid="mtr-send"
        >
          {submitting ? <CircularProgress size={20} color="inherit" /> : "Send request"}
        </Button>
      </div>
      <TextField
        className="mtr-message"
        size="small"
        variant="outlined"
        multiline
        maxRows={3}
        placeholder="Add a note (optional)"
        value={message}
        onChange={(e) => setMessage(e.target.value.slice(0, 300))}
        inputProps={{ maxLength: 300, "aria-label": "Optional message" }}
      />
    </InvitationPanel>
  );

  // ---- admin branch: all-students roster --------------------------------
  if (isAdmin) {
    const rows = students || [];
    return (
      <Box className="body-cont-over mentorships-page">
        <Menu />
        <div className="mtr-shell">
          <div className="mtr-header">
            <div className="mtr-title-row">
              <Typography variant="h5">All students</Typography>
              <Chip size="small" color="primary" label="ADMIN" className="mtr-beta" />
            </div>
            <Typography variant="body2" className="mtr-sub">
              View any student&apos;s quiz progress (read-only). Search by name or email.
            </Typography>
          </div>

          <div className="mtr-card mtr-request">
            <TextField
              className="mtr-field"
              size="small"
              variant="outlined"
              placeholder="Search students by name or email"
              value={adminSearch}
              onChange={(e) => {
                setAdminSearch(e.target.value);
                setAdminPage(0); // a new query restarts at the first page
              }}
              inputProps={{ "aria-label": "Search students", "data-testid": "mtr-admin-search" }}
            />
          </div>

          {adminError && (
            <Typography className="mtr-error" variant="body2" data-testid="mtr-admin-error">
              {adminError}
            </Typography>
          )}

          {adminLoading && !students && (
            <div className="mtr-loading" data-testid="mtr-admin-loading">
              <CircularProgress size={22} />
              <Typography variant="caption" className="mtr-sub">Loading…</Typography>
            </div>
          )}

          <div className="mtr-section" data-testid="mtr-admin-students">
            <Typography variant="subtitle2" className="mtr-section-title">
              {studentCountLabel(adminTotalElements)}
            </Typography>
            {rows.length === 0 && !adminLoading && !adminError ? (
              <Typography variant="body2" className="mtr-empty" data-testid="mtr-admin-empty">
                {adminSearch.trim() ? "No students match your search." : "No students found."}
              </Typography>
            ) : (
              rows.map((student) => (
                <AdminStudentCard key={student.studentUserId || student.email || student.displayName} student={student} history={history} />
              ))
            )}
          </div>

          {adminTotalPages > 1 && (
            <div className="mtr-section mtr-admin-pager" data-testid="mtr-admin-pager">
              <Button
                variant="outlined"
                size="small"
                disabled={adminPage <= 0 || adminLoading}
                onClick={() => setAdminPage((p) => Math.max(0, p - 1))}
                data-testid="mtr-admin-prev"
              >
                Previous
              </Button>
              <Typography variant="caption" className="mtr-sub">
                Page {adminPage + 1} of {adminTotalPages}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={adminPage >= adminTotalPages - 1 || adminLoading}
                onClick={() => setAdminPage((p) => p + 1)}
                data-testid="mtr-admin-next"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </Box>
    );
  }

  return (
    <Box className="body-cont-over mentorships-page">
      <Menu />
      <div className="mtr-shell">
        <div className="mtr-header">
          <div className="mtr-title-row">
            <Typography variant="h5">{isTutor ? "My students" : "My tutors"}</Typography>
            <Chip size="small" color="primary" label="BETA" className="mtr-beta" />
          </div>
          <Typography variant="body2" className="mtr-sub">
            {isTutor
              ? "View progress for students linked through Medlearnity tutoring sessions or accepted invitations. Either of you can stop sharing at any time."
              : "Tutors linked through your Medlearnity sessions or accepted invitations can view your quiz progress. You can stop sharing at any time."}
          </Typography>
        </div>

        {/* Request form: students see it first; tutors find it after their students. */}
        {!isTutor && invitation}

        {error && (
          <Typography className="mtr-error" variant="body2" data-testid="mtr-error">
            {error}
          </Typography>
        )}

        {loading && !list && (
          <div className="mtr-loading" data-testid="mtr-loading">
            <CircularProgress size={22} />
            <Typography variant="caption" className="mtr-sub">Loading…</Typography>
          </div>
        )}

        {/* Incoming requests to accept/decline */}
        {incoming.length > 0 && (
          <div className="mtr-section" data-testid="mtr-incoming">
            <Typography variant="subtitle2" className="mtr-section-title">
              Requests for you
            </Typography>
            {incoming.map((m) => (
              <div className="mtr-card mtr-item" key={m.id}>
                <div className="mtr-item-main">
                  <Typography className="mtr-name">{name(m)}</Typography>
                  {m.requestMessage && (
                    <Typography variant="body2" className="mtr-msg">“{m.requestMessage}”</Typography>
                  )}
                  <Typography variant="caption" className="mtr-sub">
                    wants to {isTutor ? "be followed by you" : "follow your progress"}
                  </Typography>
                </div>
                <div className="mtr-item-actions">
                  <Button
                    className="mtr-btn mtr-btn--accept"
                    variant="contained"
                    size="small"
                    disabled={busyId === m.id}
                    onClick={() => respond(m.id, "accept")}
                  >
                    Accept
                  </Button>
                  <Button
                    className="mtr-btn"
                    variant="outlined"
                    size="small"
                    disabled={busyId === m.id}
                    onClick={() => respond(m.id, "decline")}
                  >
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tutor dashboard, first: recent learning across every linked student
            (one post-session request + the one OPEN-tag request; no per-student
            fan-out). Renders nothing while both sources are flag-dark. */}
        {isTutor && (
          <RecentActivitySection
            postSession={{ ...postSession, enabled: postSessionEnabled }}
            tags={{ ...openTags, enabled: tagsEnabled, load: loadTags }}
            labels={labels}
            activeStudentIds={list ? new Set(active.map((m) => m.studentUserId).filter(Boolean)) : null}
          />
        )}

        {/* Active mentorships: for tutors a compact, searchable directory. */}
        <div className="mtr-section" data-testid="mtr-active">
          <Typography variant="subtitle2" className="mtr-section-title">
            {isTutor ? "Students you follow" : "Tutors following you"}
            {isTutor && active.length > 0 && ` (${active.length})`}
          </Typography>
          {isTutor && active.length > 0 && <div className="mtr-roster-search">
            <TextField size="small" variant="outlined" type="search" label="Search connected students"
              placeholder="Name or email" value={rosterSearch}
              onChange={event => setRosterSearch(event.target.value)}
              inputProps={{"aria-label":"Search connected students"}} />
            {rosterSearch && <Button color="inherit" size="small" onClick={() => setRosterSearch('')}>Clear student search</Button>}
            <Typography variant="caption" className="mtr-sub" role="status">
              {rosterCapped ? `Showing ${visibleActive.length} of ${active.length}` : `${matching.length} of ${active.length}`} connected students
            </Typography>
          </div>}
          {rosterQuery && matching.length === 0 && <Typography className="mtr-empty">No connected students match your search.</Typography>}
          {active.length === 0 && !loading && !error ? (
            <Typography variant="body2" className="mtr-empty" data-testid="mtr-active-empty">
              {isTutor ? "Assigned Medlearnity students appear here automatically. You can also invite an additional student below." : "No active connections yet — invite a tutor above to get started."}
            </Typography>
          ) : (
            <div className={isTutor ? "mtr-directory" : undefined}>
            {visibleActive.map(({ link: m, secondary }) => {
              // Tutors can open a linked student's progress view.
              const studentId = m.studentUserId;
              const openProgress =
                isTutor && studentId ? () => history.push(studentProgressLocation(studentId, history.location.state)) : null;
              // T4: OPEN tags this student has waiting for the caller.
              const tagCount = (studentId && openTagCounts?.[studentId]) || 0;
              return (
              <div
                className={`mtr-card mtr-item${openProgress ? " mtr-item--link" : ""}${isTutor ? " mtr-item--compact" : ""}`}
                key={m.id}
                data-testid={isTutor ? "mtr-student-row" : undefined}
              >
                <ProgressCardContent onOpen={openProgress}>
                  <span className="tag-row-head">
                    <Typography component="span" className="mtr-name">{name(m)}</Typography>
                    {secondary && <Typography component="span" variant="caption" className="mtr-sub mtr-secondary">{secondary}</Typography>}
                    {tagCount > 0 && (
                      <Chip
                        component="span"
                        size="small"
                        className="tag-chip tag-chip--open"
                        label={tagCount === 1 ? "1 tagged question" : `${tagCount} tagged questions`}
                        data-testid="mtr-tag-count"
                      />
                    )}
                  </span>
                  <Typography variant="caption" className="mtr-sub">
                    {openProgress ? "View-only · tap to see progress" : "View-only · connected"}
                  </Typography>
                </ProgressCardContent>
                <Button
                  className={`mtr-btn${isTutor ? " mtr-btn--quiet" : ""}`}
                  variant={isTutor ? "text" : "outlined"}
                  size="small"
                  disabled={busyId === m.id}
                  onClick={() => respond(m.id, "revoke")}
                  aria-label={isTutor ? `Stop sharing with ${name(m)}${secondary ? ` (${secondary})` : ""}` : undefined}
                >
                  Stop sharing
                </Button>
              </div>
            );
            })}
            </div>
          )}
          {isTutor && !rosterQuery && matching.length > ROSTER_PREVIEW && (
            <Button className="mtr-btn mtr-roster-toggle" size="small" color="inherit" aria-expanded={!rosterCapped}
              onClick={() => setRosterExpanded(!rosterExpanded)}>
              {rosterCapped ? `Show all ${matching.length} students` : "Show fewer students"}
            </Button>
          )}
        </div>

        {/* Student→tutor question tags (T3, sub-flag-gated; the tutor surface
            is the roster counts above + the section on the student page, T4).
            Section renders nothing until the student has tags. */}
        {!isTutor && CONSTANTS.mentorshipTagsEnabled && <QuestionTagsSection />}

        {/* Post-session quiz email tracker: delivery and link lifecycle,
            filters and Do together (read-only list, own flag). Tutor branch
            only — the admin branch returns above and students never reach
            this. Shares the page's single post-session request. */}
        {postSessionEnabled && <PostSessionQuizzesSection source={postSession} />}

        {isTutor && invitation}

        {/* Sent, awaiting response */}
        {outgoing.length > 0 && (
          <div className="mtr-section" data-testid="mtr-outgoing">
            <Typography variant="subtitle2" className="mtr-section-title">
              Sent · awaiting response
            </Typography>
            {outgoing.map((m) => (
              <div className="mtr-card mtr-item" key={m.id}>
                <div className="mtr-item-main">
                  <Typography className="mtr-name">{name(m)}</Typography>
                  <Typography variant="caption" className="mtr-sub">Waiting for them to accept</Typography>
                </div>
                <Button
                  className="mtr-btn"
                  variant="outlined"
                  size="small"
                  disabled={busyId === m.id}
                  onClick={() => respond(m.id, "revoke")}
                >
                  Cancel
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Box>
  );
}
